import React, { useMemo } from "react";
import get from "lodash.get";
import styled from "@emotion/styled";
import { useIntl } from "react-intl";
import { connect } from "react-redux";

import Abilities from "services/permissions/abilities";
import MapActioner from "services/map/actioner";
import ContractUtils from "services/contract/utils";
import PedestrianStatisticsActioner from "services/pedestrianStatistics/actioner";
import PedestrianStatisticsUtils from "services/pedestrianStatistics/utils";
import { GraphProvider } from "services/graphs";

import ParkkiTheme from "themes/ParkkiTheme";

import LineMultiPercentage from "components/LineMultiPercentage";
import Section from "components/Section";
import StatsChart from "components/StatsChart";
import StatsRow from "components/StatsRow";
import HourRangeSlider from "components/HourRangeSlider";
import ToggleButtonGroup from "components/ToggleButtonGroup";
import QueueComparisonTable from "components/QueueComparisonTable";
import StatsSlider from "../StatsSlider/statsSlider";
import { Paper } from "@mui/material";

const AffluenceFiltersContainer = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
`;

const AffluenceFilterSubContainer = styled.div`
  display: flex;
  flex: 1;
`;

const LineGraphSection = styled(Paper)`
  position: relative;
  display: flex;
  flex-direction: row;
  padding: ${ParkkiTheme.spacings.base};
`;

const styles = {
  statsContainer: {
    display: "flex",
    flexDirection: "row",
  },
};

const getQueueLineMultiFormattedData = (boSettings, areas, selectedAreas) => {
  if (!areas || !areas.length) return [];

  return areas.map((area) => {
    const areaDetails = selectedAreas.find(
      (selectedArea) => selectedArea.id === area.area
    );
    const color = areaDetails.options.selection_color;
    const { symbol, value } =
      PedestrianStatisticsUtils.getBackofficeSettingsConditionWithValue(
        boSettings,
        area.area,
        area.waiting_time_in_seconds / 60
      );

    return {
      label: areaDetails?.long_name,
      value: area.value,
      overridePercentage: `${symbol} ${value}min`,
      percentage:
        area.value +
        (100 - areas.reduce((acc, curr) => acc + curr.value, 0)) / areas.length, // 100 - sum of all values divided by the number of areas to always have 100%,
      color,
    };
  });
};

function PedestrianStatisticsStatsSection({
  pedestrianStatistics,
  retrieveAllPedestrianStats,
  selectedAreas,
  setCenterToDefault,
  contract,
  isPedestrianAffluenceChartTimeRangeByHour,
  setPedestrianAffluenceChartTimeRangeByDay,
  retrieveDailyAttendanceStats,
  retrieveQueueDailyAttendanceMaxStats,
  retrieveQueueDailyAttendanceStats,
  attendanceHourRange,
  setAttendanceFilter,
  setQueueAttendanceFilter,
  setQueueAttendanceMaxFilter,
  setQueueWaitingTimeFilter,
  setPedestrianQueueAffluenceMaxChartTimeRangeByDay,
  isPedestrianQueueAffluenceMaxChartTimeRangeByHour,
  setPedestrianQueueAffluenceChartTimeRangeByDay,
  isPedestrianQueueAffluenceChartTimeRangeByHour,
  isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour,
  queueAttendanceHourRange,
  retriveQueueDailyWaitingTimeStats,
  setPedestrianQueueWaitingTimeChartTimeRangeByDay,
  queueWaitingTimeRange,
}) {
  const intl = useIntl();
  const attendanceLimitTime =
    PedestrianStatisticsUtils.getAttendanceLimitTime(pedestrianStatistics);
  const queueDailyAttendanceMaxChart =
    PedestrianStatisticsUtils.getQueueAttendanceMaxChart(
      pedestrianStatistics,
      intl
    );
  const queueDailyAttendanceChart =
    PedestrianStatisticsUtils.getQueueAttendanceChart(
      pedestrianStatistics,
      intl
    );
  const queueDailyWaitingTimeChart =
    PedestrianStatisticsUtils.getQueueWaitingTimeChart(
      pedestrianStatistics,
      intl
    );

  const userBoSettingsPermissions = {
    isAllowedToSeeAreasSelection: Abilities.ability.can(
      "read",
      "pedestrian_stats.areasSelection"
    ),
    isAllowedToSeeDetails: Abilities.ability.can(
      "read",
      "pedestrian_stats.queue.details"
    ),
    isAllowedToSeeDailyAttendanceMax: Abilities.ability.can(
      "read",
      "pedestrian_stats.queue.dailyAttendanceMax"
    ),
    isAllowedToSeeDailyAttendance: Abilities.ability.can(
      "read",
      "pedestrian_stats.queue.dailyAttendance"
    ),
    isAllowedToSeeDailyWaitingTime: Abilities.ability.can(
      "read",
      "pedestrian_stats.queue.dailyWaitingTime"
    ),
  };

  const isQueueAreasSelected = pedestrianStatistics?.map?.selectedLayers?.some(
    (area) => area.apiType === "QUEUEMANAGEMENT"
  );

  const isPedestrianAreasSelected =
    pedestrianStatistics?.map?.selectedLayers?.some(
      (area) => area.apiType === "PEDESTRIANANDFLOW"
    );

  const selectedQueueAreas = pedestrianStatistics?.map?.selectedLayers
    ?.map((area) => (area.apiType === "QUEUEMANAGEMENT" ? area : null))
    .filter((area) => area !== null);

  const pedestrianAffluenceChartTimeRangeOptions = useMemo(
    () => [
      {
        label: intl.messages["pedestrianStatisticsScene.sections.byHour"],
        value: true,
      },
      {
        label:
          intl.messages["pedestrianStatisticsScene.sections.byFiveMinutes"],
        value: false,
      },
    ],
    [intl]
  );

  const pedestrianAffluenceMaxChartTimeRangeOptions = useMemo(
    () => [
      {
        label: intl.messages["pedestrianStatisticsScene.sections.byHour"],
        value: true,
      },
      {
        label:
          intl.messages["pedestrianStatisticsScene.sections.byFiveMinutes"],
        value: false,
      },
    ],
    [intl]
  );

  const statsSlider = PedestrianStatisticsUtils.getStatsSlider(
    pedestrianStatistics,
    intl
  );
  const keyNumbersStatsRow = PedestrianStatisticsUtils.getKeyNumbersStatsRow();
  const chartStats =
    PedestrianStatisticsUtils.getChartStats(pedestrianStatistics);
  const hasQueueError = useMemo(() => {
    const populatedSelectedAreas = selectedAreas.map((selectedArea) =>
      pedestrianStatistics.map.areas.find(
        (mapArea) => mapArea.area_id === selectedArea
      )
    );

    if (
      populatedSelectedAreas.some((area) => area.apiType === "QUEUEMANAGEMENT")
    ) {
      return pedestrianStatistics?.queue?.error;
    }
  }, [
    pedestrianStatistics.map.areas,
    pedestrianStatistics?.queue?.error,
    selectedAreas,
  ]);

  const hasError = useMemo(() => {
    const populatedSelectedAreas = selectedAreas.map((selectedArea) =>
      pedestrianStatistics.map.areas.find(
        (mapArea) => mapArea.area_id === selectedArea
      )
    );

    if (
      populatedSelectedAreas.some(
        (area) => area.apiType === "PEDESTRIANANDFLOW"
      )
    ) {
      return (
        pedestrianStatistics?.general?.error ||
        pedestrianStatistics?.control?.error ||
        pedestrianStatistics?.dailyAttendance?.error ||
        pedestrianStatistics?.dailyWaitingTime?.error
      );
    }
  }, [
    selectedAreas,
    pedestrianStatistics.map.areas,
    pedestrianStatistics?.general?.error,
    pedestrianStatistics?.control?.error,
    pedestrianStatistics?.dailyAttendance?.error,
    pedestrianStatistics?.dailyWaitingTime?.error,
  ]);

  function getNumberOfSection(stats = []) {
    if (stats === null) return 0;
    return stats?.filter((item) => item.toggle)?.length;
  }

  function handleAttendanceRangeChange(range, type) {
    let toHourInSeconds;
    const fromRange =
      range?.[0] !== null && range?.[0] !== undefined ? range?.[0] : 9;
    const toRange = range?.[1] || 18;

    if (toRange === 24) {
      toHourInSeconds = toRange * 3600 - 5 * 60; // 23h55
    }

    const obj = {
      fromHour: fromRange * 3600,
      toHour: toRange === 24 ? toHourInSeconds : toRange * 3600,
    };

    switch (type) {
      case "QUEUE_ATTENDANCE":
        return setQueueAttendanceFilter(obj);
      case "QUEUE_ATTENDANCE_MAX":
        return setQueueAttendanceMaxFilter(obj);
      case "QUEUE_WAITING_TIME":
        return setQueueWaitingTimeFilter(obj);
      default:
        return setAttendanceFilter(obj);
    }
  }

  return (
    <>
      {userBoSettingsPermissions.isAllowedToSeeAreasSelection &&
        isPedestrianAreasSelected &&
        getNumberOfSection(keyNumbersStatsRow) > 0 && (
          <Section>
            <StatsRow
              initialized={
                hasError ? false : pedestrianStatistics.general.initialized
              }
              stats={keyNumbersStatsRow}
              numberOfSection={getNumberOfSection(keyNumbersStatsRow)}
              containerStyles={styles.statsContainer}
            />
          </Section>
        )}

      {userBoSettingsPermissions.isAllowedToSeeAreasSelection &&
        isPedestrianAreasSelected &&
        get(statsSlider, "toggle", false) && (
          <Section
            title={intl.messages["pedestrianStatisticsScene.slider.title"]}
          >
            <StatsSlider
              initialized={
                hasError ? false : pedestrianStatistics.general.initialized
              }
              stats={statsSlider}
            />
          </Section>
        )}

      {userBoSettingsPermissions.isAllowedToSeeAreasSelection &&
        isPedestrianAreasSelected &&
        get(chartStats, "toggle", false) && (
          <GraphProvider
            timeTitle={`[${ContractUtils.formatTimestamp(
              contract,
              pedestrianStatistics?.filters?.timestamp
            )}]`}
            label={pedestrianStatistics?.map?.selectedLayers
              ?.filter((area) => area.apiType === "PEDESTRIANANDFLOW")
              ?.map((layer) => layer.long_name)
              .join(", ")}
          >
            <Section
              title={intl.messages["pedestrianStatisticsScene.sections.graph"]}
            >
              <AffluenceFiltersContainer>
                <AffluenceFilterSubContainer>
                  <ToggleButtonGroup
                    exclusive
                    loading={
                      hasError
                        ? false
                        : !pedestrianStatistics?.general.initialized
                    }
                    options={pedestrianAffluenceChartTimeRangeOptions}
                    value={isPedestrianAffluenceChartTimeRangeByHour}
                    onChange={(e, value) => {
                      if (value === null) return;
                      setPedestrianAffluenceChartTimeRangeByDay(value);
                      return retrieveDailyAttendanceStats();
                    }}
                  />
                </AffluenceFilterSubContainer>
                {!isPedestrianAffluenceChartTimeRangeByHour && (
                  <AffluenceFilterSubContainer>
                    <HourRangeSlider
                      value={attendanceHourRange}
                      onChange={handleAttendanceRangeChange}
                      min={attendanceLimitTime?.left_time_limit}
                      max={attendanceLimitTime?.right_time_limit}
                    />
                  </AffluenceFilterSubContainer>
                )}
              </AffluenceFiltersContainer>

              <StatsChart
                initialized={
                  hasError
                    ? false
                    : pedestrianStatistics.dailyAttendance.initialized
                }
                stats={chartStats}
                graphOptions={PedestrianStatisticsUtils.getAffluenceChartOptions(
                  pedestrianStatistics
                )}
              />
            </Section>
          </GraphProvider>
        )}
      {isQueueAreasSelected && (
        <>
          {userBoSettingsPermissions.isAllowedToSeeDetails &&
            !pedestrianStatistics?.queue?.error && (
              <QueueComparisonTable
                initialized={
                  pedestrianStatistics.queue.initialized &&
                  pedestrianStatistics.general.initialized
                }
                selectedAreas={selectedQueueAreas}
                pedestrianStatistics={pedestrianStatistics}
              />
            )}
          {userBoSettingsPermissions.isAllowedToSeeDetails &&
            pedestrianStatistics.queue.initialized &&
            pedestrianStatistics.general.initialized &&
            !pedestrianStatistics?.queue?.error &&
            selectedQueueAreas.length > 1 && (
              <Section
                title={
                  intl.messages[
                    "pedestrianStatisticsScene.sections.distribution"
                  ]
                }
              >
                <GraphProvider
                  timeTitle={`[${ContractUtils.formatTimestamp(
                    contract,
                    pedestrianStatistics?.filters?.timestamp
                  )}]`}
                  label={pedestrianStatistics?.map?.selectedLayers
                    ?.filter((area) => area.apiType === "QUEUEMANAGEMENT")
                    ?.map((layer) => layer.long_name)
                    .join(", ")}
                >
                  <LineGraphSection>
                    <LineMultiPercentage
                      data={getQueueLineMultiFormattedData(
                        pedestrianStatistics.config.queue_management,
                        pedestrianStatistics.queue.data,
                        pedestrianStatistics.map.selectedLayers
                      )}
                    />
                  </LineGraphSection>
                </GraphProvider>
              </Section>
            )}
          {userBoSettingsPermissions.isAllowedToSeeDailyAttendanceMax &&
            !pedestrianStatistics?.queue?.dailyAttendanceMax?.error && (
              <GraphProvider
                timeTitle={`[${ContractUtils.formatTimestamp(
                  contract,
                  pedestrianStatistics?.filters?.timestamp
                )}]`}
                label={pedestrianStatistics?.map?.selectedLayers
                  ?.filter((area) => area.apiType === "QUEUEMANAGEMENT")
                  ?.map((layer) => layer.long_name)
                  .join(", ")}
              >
                <Section
                  title={intl.formatMessage(
                    {
                      id: "pedestrianStatisticsScene.sections.queueAttendanceMaxGraph",
                    },
                    { count: selectedQueueAreas.length }
                  )}
                >
                  <AffluenceFiltersContainer>
                    <AffluenceFilterSubContainer>
                      <ToggleButtonGroup
                        exclusive
                        loading={
                          hasQueueError
                            ? true
                            : !pedestrianStatistics?.queue?.dailyAttendanceMax
                                ?.initialized
                        }
                        options={pedestrianAffluenceMaxChartTimeRangeOptions}
                        value={
                          isPedestrianQueueAffluenceMaxChartTimeRangeByHour
                        }
                        onChange={(e, value) => {
                          if (value === null) return;
                          setPedestrianQueueAffluenceMaxChartTimeRangeByDay(
                            value
                          );
                          return retrieveQueueDailyAttendanceMaxStats();
                        }}
                      />
                    </AffluenceFilterSubContainer>
                    {!isPedestrianQueueAffluenceMaxChartTimeRangeByHour && (
                      <AffluenceFilterSubContainer>
                        <HourRangeSlider
                          value={queueAttendanceHourRange}
                          onChange={(range) =>
                            handleAttendanceRangeChange(
                              range,
                              "QUEUE_ATTENDANCE_MAX"
                            )
                          }
                          min={attendanceLimitTime?.left_time_limit}
                          max={attendanceLimitTime?.right_time_limit}
                        />
                      </AffluenceFilterSubContainer>
                    )}
                  </AffluenceFiltersContainer>
                  <StatsChart
                    initialized={
                      hasQueueError
                        ? false
                        : pedestrianStatistics?.queue?.dailyAttendanceMax
                            ?.initialized &&
                          pedestrianStatistics.general.initialized
                    }
                    graphOptions={queueDailyAttendanceMaxChart.options}
                    stats={queueDailyAttendanceMaxChart.data}
                  />
                </Section>
              </GraphProvider>
            )}

          {userBoSettingsPermissions.isAllowedToSeeDailyAttendance &&
            !pedestrianStatistics?.queue?.dailyAttendance?.error && (
              <GraphProvider
                timeTitle={`[${ContractUtils.formatTimestamp(
                  contract,
                  pedestrianStatistics?.filters?.timestamp
                )}]`}
                label={pedestrianStatistics?.map?.selectedLayers
                  ?.filter((area) => area.apiType === "QUEUEMANAGEMENT")
                  ?.map((layer) => layer.long_name)
                  .join(", ")}
              >
                <Section
                  title={intl.formatMessage(
                    {
                      id: "pedestrianStatisticsScene.sections.queueAttendanceGraph",
                    },
                    { count: selectedQueueAreas.length }
                  )}
                >
                  <AffluenceFiltersContainer>
                    <AffluenceFilterSubContainer>
                      <ToggleButtonGroup
                        exclusive
                        loading={
                          hasQueueError
                            ? true
                            : !pedestrianStatistics?.queue?.dailyAttendance
                                ?.initialized
                        }
                        options={pedestrianAffluenceChartTimeRangeOptions}
                        value={isPedestrianQueueAffluenceChartTimeRangeByHour}
                        onChange={(e, value) => {
                          if (value === null) return;
                          setPedestrianQueueAffluenceChartTimeRangeByDay(value);
                          return retrieveQueueDailyAttendanceStats();
                        }}
                      />
                    </AffluenceFilterSubContainer>
                    {!isPedestrianQueueAffluenceChartTimeRangeByHour && (
                      <AffluenceFilterSubContainer>
                        <HourRangeSlider
                          value={queueAttendanceHourRange}
                          onChange={(range) =>
                            handleAttendanceRangeChange(
                              range,
                              "QUEUE_ATTENDANCE"
                            )
                          }
                          min={attendanceLimitTime?.left_time_limit}
                          max={attendanceLimitTime?.right_time_limit}
                        />
                      </AffluenceFilterSubContainer>
                    )}
                  </AffluenceFiltersContainer>
                  <StatsChart
                    initialized={
                      hasQueueError
                        ? false
                        : pedestrianStatistics?.queue?.dailyAttendance
                            ?.initialized &&
                          pedestrianStatistics.general.initialized
                    }
                    graphOptions={queueDailyAttendanceChart.options}
                    stats={queueDailyAttendanceChart.data}
                  />
                </Section>
              </GraphProvider>
            )}
          {userBoSettingsPermissions.isAllowedToSeeDailyWaitingTime &&
            !pedestrianStatistics?.queue?.dailyWaitingTime?.error && (
              <GraphProvider
                timeTitle={`[${ContractUtils.formatTimestamp(
                  contract,
                  pedestrianStatistics?.filters?.timestamp
                )}]`}
                label={pedestrianStatistics?.map?.selectedLayers
                  ?.filter((area) => area.apiType === "QUEUEMANAGEMENT")
                  ?.map((layer) => layer.long_name)
                  .join(", ")}
              >
                <Section
                  title={
                    intl.messages[
                      "pedestrianStatisticsScene.sections.queueWaitingDailyTimeGraph"
                    ]
                  }
                >
                  <AffluenceFiltersContainer>
                    <AffluenceFilterSubContainer>
                      <ToggleButtonGroup
                        exclusive
                        loading={
                          hasQueueError
                            ? true
                            : !pedestrianStatistics?.queue?.dailyWaitingTime
                                ?.initialized
                        }
                        options={pedestrianAffluenceChartTimeRangeOptions}
                        value={
                          isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour
                        }
                        onChange={(e, value) => {
                          if (value === null) return;
                          setPedestrianQueueWaitingTimeChartTimeRangeByDay(
                            value
                          );
                          return retriveQueueDailyWaitingTimeStats();
                        }}
                      />
                    </AffluenceFilterSubContainer>
                    {!isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour && (
                      <AffluenceFilterSubContainer>
                        <HourRangeSlider
                          value={queueWaitingTimeRange}
                          onChange={(range) =>
                            handleAttendanceRangeChange(
                              range,
                              "QUEUE_WAITING_TIME"
                            )
                          }
                          min={attendanceLimitTime?.left_time_limit}
                          max={attendanceLimitTime?.right_time_limit}
                        />
                      </AffluenceFilterSubContainer>
                    )}
                  </AffluenceFiltersContainer>
                  <StatsChart
                    initialized={
                      hasQueueError
                        ? false
                        : pedestrianStatistics?.queue?.dailyWaitingTime
                            ?.initialized &&
                          pedestrianStatistics.general.initialized
                    }
                    graphOptions={queueDailyWaitingTimeChart.options}
                    stats={queueDailyWaitingTimeChart.data}
                  />
                </Section>
              </GraphProvider>
            )}
        </>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  pedestrianStatistics: state.PedestrianStatistics,
  selectedAreas: get(
    PedestrianStatisticsUtils.getFilters("areas", state),
    "areas",
    []
  ),
  isPedestrianAffluenceChartTimeRangeByHour:
    state.PedestrianStatistics.filters
      .isPedestrianAffluenceChartTimeRangeByHour,
  isPedestrianQueueAffluenceChartTimeRangeByHour:
    state.PedestrianStatistics.filters
      .isPedestrianQueueAffluenceChartTimeRangeByHour,
  isPedestrianQueueAffluenceMaxChartTimeRangeByHour:
    state.PedestrianStatistics.filters
      .isPedestrianQueueAffluenceMaxChartTimeRangeByHour,
  isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour:
    state.PedestrianStatistics.filters
      .isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour,
  attendanceHourRange: PedestrianStatisticsUtils.getHourRange(
    state.PedestrianStatistics,
    "range"
  ),
  queueAttendanceHourRange: PedestrianStatisticsUtils.getHourRange(
    state.PedestrianStatistics,
    "queueRange"
  ),
  queueWaitingTimeRange: PedestrianStatisticsUtils.getHourRange(
    state.PedestrianStatistics,
    "queueWaitingTimeRange"
  ),
});

const mapDispatchToProps = (dispatch) => ({
  retrieveAllPedestrianStats: () =>
    dispatch(PedestrianStatisticsActioner.retrieveAllStats()),
  setCenterToDefault: () => dispatch(MapActioner.setCenterToDefault()),
  retrieveDailyAttendanceStats: () =>
    dispatch(PedestrianStatisticsActioner.retrieveDailyAttendanceStats()),
  setPedestrianAffluenceChartTimeRangeByDay: (value) =>
    dispatch(
      PedestrianStatisticsActioner.setFilter(
        "isPedestrianAffluenceChartTimeRangeByHour",
        value
      )
    ),
  setPedestrianQueueAffluenceMaxChartTimeRangeByDay: (value) =>
    dispatch(
      PedestrianStatisticsActioner.setFilter(
        "isPedestrianQueueAffluenceMaxChartTimeRangeByHour",
        value
      )
    ),
  setPedestrianQueueAffluenceChartTimeRangeByDay: (value) =>
    dispatch(
      PedestrianStatisticsActioner.setFilter(
        "isPedestrianQueueAffluenceChartTimeRangeByHour",
        value
      )
    ),
  setAttendanceFilter: (value) =>
    dispatch(PedestrianStatisticsActioner.setFilter("range", value)),
  setQueueAttendanceFilter: (value) =>
    dispatch(PedestrianStatisticsActioner.setFilter("queueRange", value)),
  setQueueAttendanceMaxFilter: (value) =>
    dispatch(PedestrianStatisticsActioner.setFilter("queueMaxRange", value)),
  retrieveQueueDailyAttendanceMaxStats: () =>
    dispatch(
      PedestrianStatisticsActioner.retrieveQueueDailyAttendanceMaxStats()
    ),
  retrieveQueueDailyAttendanceStats: () =>
    dispatch(PedestrianStatisticsActioner.retrieveQueueDailyAttendanceStats()),
  setQueueWaitingTimeFilter: (value) =>
    dispatch(
      PedestrianStatisticsActioner.setFilter("queueWaitingTimeRange", value)
    ),
  retriveQueueDailyWaitingTimeStats: () =>
    dispatch(PedestrianStatisticsActioner.retrieveQueueDailyWaitingTimeStats()),
  setPedestrianQueueWaitingTimeChartTimeRangeByDay: (value) => {
    dispatch(
      PedestrianStatisticsActioner.setFilter(
        "isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour",
        value
      )
    );
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PedestrianStatisticsStatsSection);
